import {useEffect} from "react";
import mqtt from "mqtt";
import {IClientOptions} from "mqtt/src/lib/client";

export type QoS = 0 | 1 | 2
export type MqttStatus = 'Connecting' | 'Connected' | 'Disconnected' | 'Offline' | 'Reconnecting' | 'Error' | 'Closed' | 'Undefined';
export type MqttError = {
    type: string,
    msg: string,
}
export type MqttData = {
    message: any,
    topic: string,
}
export type CreateMqttClientOptions = {
    ssl: boolean,
    setMqttStatus: (status: MqttStatus) => void,
    setMqttError: (error: MqttError) => void,
    uniqueId: string,
    onMessage: (topic: string, message: any) => void,
}
export type subscribeTopic = {
    topic: string,
    qos: QoS,
}
export class MqttMessage {
    public topic: string;
    public message: any;
    public sender: string;

    private constructor(topic: string, message: any, sender: string) {
        this.topic = topic;
        this.message = message;
        this.sender = sender;
    }

    public static byNexus(topic: string, message: any, sender: string): MqttMessage {
        return new MqttMessage(topic, message, sender);
    }

    public static byMqtt(topic: string, message: any): MqttMessage {
        let rawMessage = {
            message: '',
            sender: '',
        }

        const msg = String(message.toString());

        if (msg.startsWith('{') && msg.endsWith('}')) {
            const jsonObject = JSON.parse(msg);

            if (jsonObject.message !== undefined) {
                rawMessage.message = jsonObject.message;

                if (typeof rawMessage.message === 'string' && rawMessage.message.startsWith('{') && rawMessage.message.endsWith('}')) {
                    rawMessage.message = JSON.parse(rawMessage.message);
                }
            } else {
                rawMessage.message = jsonObject.toString()
            }

            if (jsonObject.sender !== undefined) {
                rawMessage.sender = jsonObject.sender;
            }
        } else {
            rawMessage.message = message.toString();
        }
        return new MqttMessage(topic, rawMessage.message, rawMessage.sender);
    }

    public getJsonMessage(): string {
        return JSON.stringify({
            message: this.message,
            sender: this.sender,
        });
    }
}

export function useMQTTSubscribe(client: mqtt.MqttClient | null, topic: string) {
    useEffect(() => {
        if (!client || !client.connected) return;

        client.subscribe(topic);

        return () => {
            client.unsubscribe(topic);
        };
    }, [client, topic]);
}

export function useMQTTPublish(client: mqtt.MqttClient | null) {
    return (topic: string, message: string, qos: QoS, retain: boolean) => {
        if (client && client.connected) {
            client.publish(topic, message, {qos: qos, retain: retain});
        }
    };
}

export function createMqttClient(
    clientId: string,
    username: string,
    password: string,
    host: string,
    port: number,
    useLwt: boolean = true,
    setMqttStatus: (status: MqttStatus) => void,
    onMessage: (mqttMessage: MqttMessage) => void
): mqtt.MqttClient {
    const defaultConnectOptions: IClientOptions = {
        clientId: clientId,
        username: username,
        password: password,
        protocol: 'ws',
        host: host,
        port: port,
        path: '/ws',
        keepalive: 60,
        protocolVersion: 5,
        reconnectPeriod: 5000,
        queueQoSZero: true,
        resubscribe: true,
        connectTimeout: 30 * 1000,
        clean: true,
        properties: {sessionExpiryInterval: 600},
        will: undefined
    }

    if (useLwt) {
        defaultConnectOptions.will = {
            topic: '0010/jacken/lwt/' + clientId,
            payload: 'offline',
            qos: 2,
            retain: true,
        }
    }

    return mqtt.connect(defaultConnectOptions)
        .on('connect', () => {
            setMqttStatus('Connected')
        })
        .on('error', (error) => {
            console.log(error)
            setMqttStatus('Error')
        })
        .on('disconnect', (packet) => {
            setMqttStatus('Disconnected')
        })
        .on('offline', () => {
            setMqttStatus('Offline')
        })
        .on('reconnect', () => {
            setMqttStatus('Reconnecting')
        })
        .on('close', () => {
            setMqttStatus('Closed')
        })
        .on('message', (topic, message, packet) => {
            onMessage(MqttMessage.byMqtt(topic, message.toString()))
        })
}
